import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Button, Select } from 'antd';
import { TeamOutlined } from '@ant-design/icons';
import { LuDownload } from 'react-icons/lu';
import { MdAdd } from 'react-icons/md';
import dayjs from 'dayjs';

import Loader from '../ReusableComponents/Loader';
import SearchInput from '../ReusableComponents/SearchInput';
import TmFilter from '../ReusableComponents/TmFilter';
import TmMasterTable from '../ReusableComponents/TmMasterTable';
import SkillManagerModal from './SkillManagerModal';
import SkillMatrixApi from '../../Api/SkillMatrixApi';
import EmployeesReviewApi from '../../Api/EmployeesReviewApi';
import { toastError } from '../Toast/ToastComponent';
import { MONTHS } from '../../constant/constant';
import { generateYears } from '../../ReusableFn/reusableFn';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

import style from '../../Styles/Report.module.css';
import {
  getSkillMatrix,
  getNotificationHistory,
  getAttendance,
  getDepartmentReport,
  getUserReport,
  getSurveyReport,
  getVariance,
} from '../../store/reducers/actionPermission';

const CURRENT_MONTH = dayjs().format('MMMM');
const CURRENT_YEAR = dayjs().year();

function initialsFromName(name) {
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const RATING_OPTIONS = [1, 2, 3, 4, 5].map((n) => ({
  value: n,
  label: String(n),
}));

function RatingCellEditable({
  value,
  employeeId,
  skillKey,
  canEdit,
  onCommit,
}) {
  const [editing, setEditing] = useState(false);
  let v = Number(value);
  if (Number.isNaN(v)) v = 0;
  const toneClass = v ? style[`rating${v}`] || '' : '';

  const handleDoubleClick = (e) => {
    e.stopPropagation();
    if (!canEdit) return;
    setEditing(true);
  };

  const commit = (next) => {
    let n = Number(next);
    if (Number.isNaN(n)) n = 1;
    n = Math.min(5, Math.max(1, n));
    onCommit(employeeId, skillKey, n);
    setEditing(false);
  };

  if (editing) {
    return (
      <Select
        size="small"
        style={{ width: 52 }}
        value={v}
        options={RATING_OPTIONS}
        autoFocus
        open
        getPopupContainer={() => document.body}
        onChange={(val) => commit(val)}
        onOpenChange={(open) => {
          if (!open) setEditing(false);
        }}
      />
    );
  }

  return (
    <div
      className={`${style.ratingBox} ${toneClass}`}
      aria-label={`Rating ${v} out of 5`}
      onDoubleClick={handleDoubleClick}
      title={canEdit ? 'Double-click to edit' : undefined}
      style={{ cursor: canEdit ? 'pointer' : 'default' }}
    >
      {v || '--'}
    </div>
  );
}

function useSkillMatrixRouteAccess() {
  const skillMatrix = useSelector(getSkillMatrix);
  const notificationHistory = useSelector(getNotificationHistory);
  const attendance = useSelector(getAttendance);
  const departmentReport = useSelector(getDepartmentReport);
  const userReport = useSelector(getUserReport);
  const surveyReport = useSelector(getSurveyReport);
  const variance = useSelector(getVariance);

  const slices = useMemo(
    () => [
      skillMatrix,
      notificationHistory,
      attendance,
      departmentReport,
      userReport,
      surveyReport,
      variance,
    ],
    [
      skillMatrix,
      notificationHistory,
      attendance,
      departmentReport,
      userReport,
      surveyReport,
      variance,
    ],
  );

  const permissionKnown = slices.some((p) => p !== undefined);
  const canAccess = slices.some((p) => p?.checked);

  return { permissionKnown, canAccess };
}

const SkillMatrixComp = () => {
  const navigate = useNavigate();

  const permission = useSelector(getSkillMatrix);
  const { permissionKnown, canAccess } = useSkillMatrixRouteAccess();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [filterArr, setFilterArr] = useState({
    search: '',
    month: CURRENT_MONTH,
    year: CURRENT_YEAR,
  });
  const filterArrRef = useRef(filterArr);
  const [searchInputKey, setSearchInputKey] = useState(0);

  const yearOptions = useMemo(() => generateYears() || [], []);

  const skillMatrixFilterArray = useMemo(
    () => [
      {
        options: MONTHS,
        placeholder: 'Month',
        controlName: 'month',
        input: 'select',
        defaultValue: CURRENT_MONTH,
        col: 2,
        size: 'small',
      },
      {
        options: yearOptions,
        placeholder: 'Year',
        controlName: 'year',
        input: 'select',
        defaultValue: CURRENT_YEAR,
        col: 2,
        size: 'small',
      },
      {
        order: 1,
        col: 'auto',
        className: 'ms-auto pe-0',
        input: 'button',
        type: 'dashed',
        htmlType: 'button',
        text: 'Reset',
      },
    ],
    [yearOptions],
  );

  const [buttonPermission, setButtonPermissions] = useState({
    add: true,
    edit: true,
    delete: false,
    view: true,
  });

  const [skills, setSkills] = useState([]);
  const [skillModalOpen, setSkillModalOpen] = useState(false);

  useEffect(() => {
    filterArrRef.current = filterArr;
  }, [filterArr]);

  const visibleSkills = useMemo(
    () => skills.filter((s) => s.selected ?? !!s.activeStatus),
    [skills],
  );

  const mergeSkillsFromManagerReviewResult = useCallback((reviewId, result) => {
    const populated = result?.manager_review_populated;
    if (Array.isArray(populated)) {
      const skillsObj = {};
      populated
        .filter((s) => s.activeStatus)
        .forEach((skill) => {
          skillsObj[String(skill.id)] = skill.value ?? 0;
        });
      setData((prev) =>
        prev.map((emp) =>
          emp.id === reviewId ? { ...emp, skills: skillsObj } : emp,
        ),
      );
      return;
    }
    const ratingArr = result?.manager_review?.rating;
    if (Array.isArray(ratingArr) && ratingArr.length > 0) {
      setData((prev) =>
        prev.map((emp) => {
          if (emp.id !== reviewId) return emp;
          const next = { ...(emp.skills || {}) };
          ratingArr.forEach((r) => {
            if (r && r.id != null) next[String(r.id)] = Number(r.value) || 0;
          });
          return { ...emp, skills: next };
        }),
      );
    }
  }, []);

  const updateSkillRating = useCallback(
    async (employeeId, skillKey, newValue) => {
      let previousValue = 0;

      setData((prev) =>
        prev.map((emp) => {
          if (emp.id !== employeeId) return emp;
          previousValue = Number(emp.skills?.[skillKey]) || 0;
          return {
            ...emp,
            skills: { ...(emp.skills || {}), [skillKey]: newValue },
          };
        }),
      );

      const skillId = Number(skillKey);
      if (Number.isNaN(skillId)) {
        toastError('Invalid skill.');
        return;
      }

      try {
        const res = await EmployeesReviewApi.updateManagerReviewRatings({
          review_id: employeeId,
          rating: [{ id: skillId, value: newValue }],
        });
        const result = res?.data?.result ?? res?.data?.payload;
        if (result) {
          mergeSkillsFromManagerReviewResult(employeeId, result);
        }
      } catch (error) {
        console.error('Error updating manager review ratings:', error);
        toastError(
          error?.response?.data?.message ||
            'Failed to save rating. Try again.',
        );
        setData((prev) =>
          prev.map((emp) =>
            emp.id === employeeId
              ? {
                  ...emp,
                  skills: {
                    ...(emp.skills || {}),
                    [skillKey]: previousValue,
                  },
                }
              : emp,
          ),
        );
      }
    },
    [mergeSkillsFromManagerReviewResult],
  );

  const canEditRating = buttonPermission.edit;

  const skillMatrixHeaders = useMemo(() => {
    const employeeCol = {
      field: 'employee_meta',
      headerName: 'EMPLOYEE',
      className: style.skillMatrixThEmployee,
      render: (_, row) => (
        <div className={style.employeeCell}>
          <div className={style.employeeAvatar} aria-hidden>
            {initialsFromName(row.name)}
          </div>
          <div className={style.employeeMeta}>
            <p className={style.employeeName}>
              {row.name} <span className={style.employeeId}>{row.empCode}</span>
            </p>

            <div className={style.employeeTags}>
              {(row.tags || []).map((tag) => (
                <span key={tag} className={style.employeeTag}>
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </div>
      ),
    };
    const skillCols = visibleSkills.map((c) => ({
      field: c.key,
      headerName: c.label,
      align: 'center',
      className: style.skillMatrixTdRating,
      render: (val, row) => (
        <RatingCellEditable
          value={val}
          employeeId={row.id}
          skillKey={c.key}
          canEdit={canEditRating}
          onCommit={updateSkillRating}
        />
      ),
    }));

    const avgCol = {
      field: 'average',
      headerName: 'Average',
      align: 'center',
      className: style.skillMatrixTdRating,
      render: (_, row) => {
        const values = visibleSkills
          .map((s) => Number(row.skills?.[s.key] || 0))
          .filter((v) => v > 0);
        if (values.length === 0) return <span>--</span>;
        const avg = values.reduce((a, b) => a + b, 0) / values.length;
        return <span className={style.avgValue}>{avg.toFixed(1)}</span>;
      },
    };

    return [employeeCol, ...skillCols, avgCol];
  }, [visibleSkills, canEditRating, updateSkillRating]);

  const normalizeSkillRow = useCallback(
    (s) => ({
      ...s,
      key: String(s.id),
      label: s.skill_name,
      selected: !!s.activeStatus,
    }),
    [],
  );

  const loadMatrixData = useCallback(
    async (month, year) => {
      try {
        setLoading(true);

        // Fetch skills list for the modal/management
        const skillResponse = await SkillMatrixApi.getList({});
        const skillList = Array.isArray(skillResponse?.data?.result)
          ? skillResponse.data.result
          : [];
        setSkills(skillList.map(normalizeSkillRow));

        // Fetch reviews to get employee skill matrix data
        const reviewResponse = await EmployeesReviewApi.getAllUserReviews({
          month,
          year,
        });
        const reviews = Array.isArray(reviewResponse?.data?.result)
          ? reviewResponse.data.result
          : [];

        // Build employee rows from reviews
        const employeeRows = reviews.map((review) => {
          const user = review.user || {};
          const userName = [user.first_name, user.last_name]
            .filter(Boolean)
            .join(' ');

          // Get skills from manager_review_populated, filter by activeStatus: true
          const populatedSkills = Array.isArray(review.manager_review_populated)
            ? review.manager_review_populated.filter((s) => s.activeStatus)
            : [];

          // Build skills object { [skillId]: value }
          const skillsObj = {};
          populatedSkills.forEach((skill) => {
            skillsObj[String(skill.id)] = skill.value ?? 0;
          });

          return {
            id: review.id,
            user_id: review.user_id ?? user.id ?? null,
            name: userName || '--',
            empCode: user.emp_code || `EMP${user.id || ''}`,
            tags: [review.designation_name, review.department_name].filter(
              Boolean,
            ),
            skills: skillsObj,
          };
        });

        setData(employeeRows);
      } catch (error) {
        console.error('Error loading skill matrix:', error);
        toastError(
          error?.response?.data?.message || 'Failed to load skill matrix.',
        );
        setSkills([]);
        setData([]);
      } finally {
        setLoading(false);
      }
    },
    [normalizeSkillRow],
  );

  useEffect(() => {
    if (permissionKnown && !canAccess) {
      navigate('/');
    }
  }, [permissionKnown, canAccess, navigate]);

  useEffect(() => {
    if (permission && permission.actions) {
      const obj = {
        add: false,
        edit: false,
        delete: false,
        view: false,
      };
      permission.actions.forEach((el) => {
        obj[el.statename.toLowerCase()] = el.checked;
      });
      setButtonPermissions(obj);
    }
  }, [permission]);

  useEffect(() => {
    loadMatrixData(filterArr.month, filterArr.year);
  }, [filterArr.month, filterArr.year, loadMatrixData]);

  const filteredRows = useMemo(() => {
    const q = filterArr.search.trim().toLowerCase();
    if (!q) return data;
    return data.filter((row) => {
      const inName = row.name.toLowerCase().includes(q);
      const inId = row.empCode.toLowerCase().includes(q);
      const inTags = row.tags.some((t) => t.toLowerCase().includes(q));
      return inName || inId || inTags;
    });
  }, [data, filterArr.search]);

  const tableRows = useMemo(
    () =>
      filteredRows.map((r) => ({
        ...r,
        employee_meta: true,
        ...r.skills,
      })),
    [filteredRows],
  );

  const handleSearchChange = (value) => {
    setFilterArr((prev) => ({ ...prev, search: value }));
  };

  const handleFilterChange = (value) => {
    const merged = { ...filterArrRef.current, ...value };
    setFilterArr(merged);
    if (value && Object.prototype.hasOwnProperty.call(value, 'search')) {
      setSearchInputKey((k) => k + 1);
    }
  };

  const openSkillModal = useCallback(() => {
    setSkillModalOpen(true);
  }, []);

  const closeSkillModal = useCallback(() => {
    setSkillModalOpen(false);
  }, []);

  /**
   * Called by SkillManagerModal after the user clicks "Save Changes"
   * (the modal already persisted the changes via API).
   */
  const handleSkillsSaved = useCallback(
    (savedSkills) => {
      const normalized = (savedSkills || []).map(normalizeSkillRow);
      setSkills(normalized);

      setData((prev) =>
        prev.map((emp) => {
          const nextSkills = { ...(emp.skills || {}) };
          const allowed = new Set(normalized.map((s) => s.key));
          normalized.forEach((s) => {
            if (nextSkills[s.key] === undefined) nextSkills[s.key] = 0;
          });
          Object.keys(nextSkills).forEach((k) => {
            if (!allowed.has(k)) delete nextSkills[k];
          });
          return { ...emp, skills: nextSkills };
        }),
      );
    },
    [normalizeSkillRow],
  );

  const handleDownload = useCallback(async () => {
    if (filteredRows.length === 0) {
      toastError('No data to download.');
      return;
    }

    const RATING_COLORS = {
      1: 'FFFF6B6B',
      2: 'FFFFC000',
      3: 'FF92D050',
      4: 'FF00B050',
      5: 'FF385723',
    };
    const RATING_FONT_COLORS = {
      1: 'FFFFFFFF',
      2: 'FF000000',
      3: 'FF000000',
      4: 'FFFFFFFF',
      5: 'FFFFFFFF',
    };

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Skill Matrix');

    const baseColumns = [
      { header: 'S.No', key: 'sno', width: 6 },
      { header: 'Employee ID', key: 'empCode', width: 14 },
      { header: 'Employee Name', key: 'name', width: 26 },
      { header: 'Department', key: 'department', width: 22 },
      { header: 'Designation', key: 'designation', width: 26 },
    ];

    const skillColumns = visibleSkills.map((s) => ({
      header: s.label,
      key: `skill_${s.key}`,
      width: 6,
    }));

    const avgColumn = { header: 'Average', key: 'average', width: 12 };

    worksheet.columns = [...baseColumns, ...skillColumns, avgColumn];

    filteredRows.forEach((row, index) => {
      const skillValues = visibleSkills.map(
        (s) => Number(row.skills?.[s.key]) || 0,
      );
      const nonZero = skillValues.filter((v) => v > 0);
      const avg =
        nonZero.length > 0
          ? nonZero.reduce((a, b) => a + b, 0) / nonZero.length
          : 0;

      const rowData = {
        sno: index + 1,
        empCode: row.empCode || '',
        name: row.name || '',
        department: row.tags?.[1] || '',
        designation: row.tags?.[0] || '',
        average: avg ? Number(avg.toFixed(2)) : '--',
      };
      visibleSkills.forEach((s, i) => {
        rowData[`skill_${s.key}`] = skillValues[i] || '';
      });

      const dataRow = worksheet.addRow(rowData);
      dataRow.height = 22;

      dataRow.eachCell((cell, colNumber) => {
        cell.border = {
          top: { style: 'thin', color: { argb: 'FF000000' } },
          left: { style: 'thin', color: { argb: 'FF000000' } },
          bottom: { style: 'thin', color: { argb: 'FF000000' } },
          right: { style: 'thin', color: { argb: 'FF000000' } },
        };
        cell.font = {
          name: 'Calibri',
          size: 11,
          color: { argb: 'FF000000' },
        };
        cell.alignment = {
          horizontal: 'center',
          vertical: 'middle',
        };

        const isSkillCol =
          colNumber > baseColumns.length &&
          colNumber <= baseColumns.length + visibleSkills.length;

        if (isSkillCol) {
          const value = Number(cell.value);
          if (value >= 1 && value <= 5) {
            cell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: RATING_COLORS[value] },
            };
            cell.font = {
              name: 'Calibri',
              size: 11,
              bold: true,
              color: { argb: RATING_FONT_COLORS[value] },
            };
          }
        }

        if (cell._column?._key === 'name') {
          cell.alignment = { horizontal: 'left', vertical: 'middle' };
        }
      });
    });

    const headerRow = worksheet.getRow(1);
    headerRow.height = 90;
    headerRow.eachCell((cell, colNumber) => {
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFFFC000' },
      };
      cell.font = {
        name: 'Calibri',
        size: 11,
        bold: true,
        color: { argb: 'FF000000' },
      };
      cell.border = {
        top: { style: 'thin', color: { argb: 'FF000000' } },
        left: { style: 'thin', color: { argb: 'FF000000' } },
        bottom: { style: 'thin', color: { argb: 'FF000000' } },
        right: { style: 'thin', color: { argb: 'FF000000' } },
      };

      const isSkillCol =
        colNumber > baseColumns.length &&
        colNumber <= baseColumns.length + visibleSkills.length;

      cell.alignment = {
        horizontal: 'center',
        vertical: 'middle',
        wrapText: true,
        textRotation: isSkillCol ? 90 : 0,
      };
    });

    worksheet.autoFilter = {
      from: { row: 1, column: 1 },
      to: { row: 1, column: worksheet.columns.length },
    };
    worksheet.views = [{ state: 'frozen', xSplit: 5, ySplit: 1 }];

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(
      new Blob([buffer]),
      `Skill_Matrix_${filterArr.month}_${filterArr.year}.xlsx`,
    );
  }, [filteredRows, visibleSkills, filterArr.month, filterArr.year]);

  if (permissionKnown && !canAccess) {
    return null;
  }

  return (
    <>
      <div className="d-md-flex align-items-center mb-2 flex-wrap gap-2">
        <div className="d-flex align-items-center flex-wrap gap-2">
          <h5 className="mb-0">Skill Matrix Overview</h5>
          <span className={style.skillMatrixUserPill}>
            <TeamOutlined />
            <span>All Users</span>
            <span className={style.skillMatrixUserPillCount}>
              {data.length}
            </span>
          </span>
        </div>
        <div className="ms-auto d-flex align-items-center flex-wrap gap-2">
          <div className="me-2" style={{ minWidth: 220 }}>
            <SearchInput
              key={searchInputKey}
              placeholder="Search by name, ID, or tag"
              value={filterArr.search}
              onSearchChange={handleSearchChange}
            />
          </div>

          <div className="mt-2 d-flex justify-content-between mt-md-0">
            <div>
              <Button
                type="dashed"
                className="me-1"
                htmlType="button"
                icon={<LuDownload size={18} />}
                onClick={handleDownload}
              >
                Download
              </Button>
            </div>
            <div>
              <Button
                title="Add Skill"
                type="primary"
                icon={<MdAdd size={20} />}
                onClick={openSkillModal}
                disabled={!buttonPermission?.add}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="mt-2 mb-2">
        <TmFilter
          filterArray={skillMatrixFilterArray}
          handleFilterChange={handleFilterChange}
          clearDatesOnReset
        />
      </div>

      {loading ? (
        <Loader />
      ) : (
        <TmMasterTable
          rows={tableRows}
          columns={skillMatrixHeaders}
          custom={[]}
          bordered
        />
      )}

      <SkillManagerModal
        open={skillModalOpen}
        onClose={closeSkillModal}
        onSaved={handleSkillsSaved}
      />
    </>
  );
};

export default SkillMatrixComp;
